import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import latestVideos from '../data/latest-videos.json';

const LatestVideos: React.FC = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  
  if (!latestVideos || latestVideos.length === 0) {
    return (
      <section className="py-16 bg-zinc-950 text-white">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-3xl font-semibold mb-4">Latest YouTube Posts</h2>
          <p className="text-zinc-400">Fetching the newest uploads... Please rebuild once the fetch script runs.</p>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="py-16 bg-zinc-950 text-white relative overflow-hidden">
      {/* Subtle background accent */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-4xl h-px bg-gradient-to-r from-transparent via-yellow-400/20 to-transparent" />
      
      <div className="mx-auto max-w-6xl px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="space-y-3 mb-12"
        >
          <p className="text-xs uppercase tracking-[0.4em] text-yellow-400 font-semibold">YouTube</p>
          <div className="flex items-end justify-between">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Latest Videos</h2>
              <p className="text-zinc-500 mt-2">Fresh uploads, every week.</p>
            </div>
            <a
              href="https://www.youtube.com/@cryptoindia"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition-colors group"
            >
              <span className="text-sm font-medium">View all</span>
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </motion.div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {latestVideos.map((video: any, index: number) => (
            <motion.a
              key={video.id}
              href={video.url}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group relative overflow-hidden rounded-2xl border border-zinc-800/50 bg-zinc-900/50 backdrop-blur-sm transition-all duration-300 hover:border-yellow-400/40 hover:bg-zinc-900"
            >
              {/* Glow on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-yellow-400/5 to-transparent" />
              
              <div className="relative">
                <div className="relative h-48 w-full overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-transparent to-transparent" />
                  
                  {video.duration && (
                    <span className="absolute bottom-3 right-3 rounded-lg bg-black/80 backdrop-blur-sm px-2.5 py-1 text-xs font-mono font-medium text-white">
                      {video.duration}
                    </span>
                  )}
                  
                  {/* Play indicator */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-14 h-14 rounded-full bg-yellow-400/90 flex items-center justify-center shadow-lg shadow-yellow-400/30">
                      <svg className="w-6 h-6 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
                
                <div className="p-5">
                  <h3 className="text-base font-semibold leading-snug text-white line-clamp-2 group-hover:text-yellow-400 transition-colors">
                    {video.title}
                  </h3>
                  <div className="mt-3 flex items-center gap-2 text-sm text-zinc-500">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                    <span>{video.view_count ? `${video.view_count.toLocaleString()} views` : 'Views hidden'}</span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="mt-10 text-center md:hidden"
        >
          <a
            href="https://www.youtube.com/@cryptoindia"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition-colors"
          >
            <span>View all on YouTube</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default LatestVideos;
